import {
    computed,
    type ComputedRef,
    type MaybeRefOrGetter,
    ref,
    toValue,
    watch,
} from 'vue'
import { clamp, type ReadonlyRef, type RefOrGetter } from '@/helpers'
import { getTimeout } from '@/helpers/globals.ts'
import {
    useOpacityStyle,
    usePercentOpacity,
} from '@/composables/motion/opacity.ts'
import type {
    CssTransitionDto,
    CssTransitionEasing,
} from '@/composables/motion/css-transition.ts'

export function usePinchPercentClosed(options: {
    scale: RefOrGetter<number>
    threshold: MaybeRefOrGetter<number>
}): ComputedRef<number> {
    return computed(() => {
        const scale = toValue(options.scale)
        const threshold = toValue(options.threshold)

        const scaleRange = 1 - threshold

        const actual = scale - threshold

        return 1 - clamp(actual / scaleRange, 0, 1)
    })
}

interface PinchToClose {
    style: ComputedRef<string>
    opacityStyle: ComputedRef<string>
    transition: ComputedRef<CssTransitionDto | undefined>
    willClose: ReadonlyRef<boolean>
    percentClosed: ComputedRef<number>
    isTransitioning: ReadonlyRef<boolean>
}

interface PinchToCloseOptions {
    scale: RefOrGetter<number>
    isPinching: RefOrGetter<boolean>
    threshold: MaybeRefOrGetter<number>
    close: () => void
    release?: () => void
    transitionTime?: MaybeRefOrGetter<number>
    transitionEasing?: MaybeRefOrGetter<CssTransitionEasing>
}

export function usePinchClose(options: PinchToCloseOptions): PinchToClose {
    const withDefaults: Required<PinchToCloseOptions> = {
        release: () => {},
        transitionTime: 200,
        transitionEasing: 'ease-in-out',
        ...options,
    }

    const {
        scale,
        isPinching,
        threshold,
        close,
        release,
        transitionTime,
        transitionEasing,
    } = withDefaults

    const percentClosed = usePinchPercentClosed({
        scale,
        threshold: threshold,
    })
    const pinchOpacity = usePercentOpacity({
        percent: percentClosed,
        endOpacity: 0.2,
    })

    const isTransitioning = ref(false)

    function shouldRelease(scale: number): boolean {
        return scale >= toValue(threshold)
    }

    const timeout = getTimeout()

    if (timeout) {
        const { setTimeout, clearTimeout } = timeout

        let timeoutId: number = 0

        watch(isPinching, (isPinching) => {
            clearTimeout(timeoutId)

            if (!isPinching) {
                if (shouldRelease(toValue(scale))) {
                    isTransitioning.value = true

                    timeoutId = setTimeout(() => {
                        isTransitioning.value = false
                    }, toValue(transitionTime))

                    release()
                } else {
                    close()
                }
            } else {
                isTransitioning.value = false
            }
        })
    }

    // This is a separate listener because we allow mouse scroll wheel to zoom in and out
    // And if we watch scale in the above listener, it will always call release() and zoom back in
    watch([scale, isPinching], ([scale, isPinching]) => {
        if (isPinching) {
            return
        }

        if (!shouldRelease(scale)) {
            close()
        }
    })

    const willClose = ref(false)

    watch(scale, (scale) => {
        willClose.value = scale < toValue(threshold)
    })

    const transitionTimeComputed = computed(() => {
        if (isTransitioning.value) {
            return toValue(transitionTime)
        }

        return 0
    })

    const { style, transition, opacityStyle } = useOpacityStyle({
        opacity: pinchOpacity,
        transitionTime: transitionTimeComputed,
        transitionEasing: transitionEasing,
    })

    return {
        style,
        transition,
        opacityStyle,
        willClose,
        percentClosed,
        isTransitioning,
    }
}
